import React, { useEffect, useState, useCallback } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip as RTooltip } from "recharts";
import { ShieldCheck, Users, Gift, Wallet, TrendingUp, Search, Loader2, Check, X, BadgeCheck, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { adminApi } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const OverviewTile = ({ label, value, accent, testid }) => (
    <div className="rounded-2xl border border-border bg-surface-1 p-4" data-testid={testid}>
        <div className="mono-label text-t4">{label}</div>
        <div className="mt-1.5 font-sans-display text-2xl font-semibold text-t1" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
);

const Pill = ({ tone = "muted", children }) => {
    const tones = {
        success: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
        warning: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
        info: "bg-[hsl(var(--info,206_70%_52%))]/15 text-[hsl(var(--info,206_70%_52%))]",
        primary: "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]",
        muted: "bg-surface-3 text-t3",
    };
    return <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium", tones[tone] || tones.muted)}>{children}</span>;
};

// ----- Overview tab -----
const OverviewTab = () => {
    const { t } = useTranslation();
    const [d, setD] = useState(null);
    useEffect(() => { (async () => { try { setD(await adminApi.overview()); } catch (e) { /* */ } })(); }, []);
    if (!d) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;
    const u = d.users || {}; const r = d.referrals || {};
    return (
        <div className="space-y-5" data-testid="admin-overview">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                <OverviewTile testid="admin-stat-total-users" label={t("admin.overview.totalUsers")} value={u.total ?? 0} />
                <OverviewTile label={t("admin.overview.free")} value={u.free ?? 0} />
                <OverviewTile label={t("admin.overview.pro")} value={u.pro ?? 0} accent="hsl(174,72%,46%)" />
                <OverviewTile label={t("admin.overview.premium")} value={u.premium ?? 0} accent="hsl(38,92%,58%)" />
                <OverviewTile label={t("admin.overview.paying")} value={u.paying ?? 0} accent="hsl(152,52%,46%)" />
                <OverviewTile testid="admin-stat-mrr" label={t("admin.overview.mrr")} value={`${d.revenue?.mrr_eur ?? 0} €`} accent="hsl(174,72%,46%)" />
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-2xl border border-border bg-surface-1 p-5 lg:col-span-2">
                    <div className="mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" /><h3 className="font-sans-display text-sm font-semibold text-t1">{t("admin.overview.growth")}</h3></div>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={r.growth || []}>
                            <XAxis dataKey="label" tick={{ fill: "hsl(215 10% 58%)", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <RTooltip cursor={{ fill: "hsl(222 14% 16%)" }} contentStyle={{ background: "hsl(222 16% 12%)", border: "1px solid hsl(222 14% 20%)", borderRadius: 8, fontSize: 12, color: "#fff" }} />
                            <Bar dataKey="count" fill="hsl(174,72%,46%)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="mt-2 flex gap-4 text-xs text-t3">
                        <span>{t("admin.overview.referrals")}: <span className="font-semibold text-t1">{r.total ?? 0}</span></span>
                        <span>{t("admin.overview.activeReferrals")}: <span className="font-semibold text-t1">{r.active ?? 0}</span></span>
                        <span>{t("admin.overview.pendingPayouts")}: <span className="font-semibold text-t1">{d.payouts?.pending_total_eur ?? 0} €</span></span>
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-surface-1 p-5">
                    <div className="mb-3 flex items-center gap-2"><Crown className="h-4 w-4 text-[hsl(var(--primary))]" /><h3 className="font-sans-display text-sm font-semibold text-t1">{t("admin.overview.topAmbassadors")}</h3></div>
                    {(d.top_ambassadors || []).length === 0 ? (
                        <p className="text-sm text-t4">{t("admin.overview.noAmbassadors")}</p>
                    ) : (
                        <div className="space-y-2">
                            {d.top_ambassadors.map((a) => (
                                <div key={a.ambassador_id} className="flex items-center justify-between rounded-xl bg-surface-2 px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-t1">{a.name}</div>
                                        <div className="mono-label text-[10px] text-t4">{a.referral_code} · {a.active} {t("admin.overview.activeMembers")}</div>
                                    </div>
                                    <span className="font-sans-display text-sm font-semibold text-[hsl(var(--primary))]">{a.monthly_commission_eur} €</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ----- Users tab -----
const UsersTab = () => {
    const { t } = useTranslation();
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (query = "") => {
        setLoading(true);
        try { const d = await adminApi.users(query); setRows(d.users || []); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(""); }, [load]);

    const onSearch = (e) => { e.preventDefault(); load(q); };

    const setPlan = async (id, tier) => {
        try { const u = await adminApi.updateUser(id, { tier }); setRows((rs) => rs.map((r) => r.id === id ? u : r)); toast.success(t("admin.users.updated")); }
        catch (e) { toast.error("Error"); }
    };
    const togglePaying = async (u) => {
        try { const nu = await adminApi.updateUser(u.id, { is_paying: !u.is_paying }); setRows((rs) => rs.map((r) => r.id === u.id ? nu : r)); toast.success(t("admin.users.updated")); }
        catch (e) { toast.error("Error"); }
    };
    const createPayout = async (u) => {
        try { await adminApi.createPayout({ ambassador_id: u.id }); toast.success(t("admin.payouts.created")); }
        catch (e) { toast.error("Error"); }
    };

    return (
        <div data-testid="admin-users">
            <form onSubmit={onSearch} className="mb-4 flex gap-2">
                <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-t4" />
                    <Input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.users.search")} className="bg-surface-2 pl-9" />
                </div>
                <Button type="submit" variant="secondary" className="bg-surface-3/70">{t("admin.users.actions")}</Button>
            </form>

            {loading ? (
                <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
            ) : rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-10 text-center text-sm text-t3">{t("admin.users.empty")}</div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-surface-1">
                    <table className="w-full min-w-[680px] text-sm">
                        <thead>
                            <tr className="border-b border-border text-left">
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.users.name")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.users.plan")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.users.paying")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.users.joined")}</th>
                                <th className="px-4 py-3 mono-label text-t4 text-right">{t("admin.users.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.id} className="border-b border-border/60 last:border-0" data-testid="admin-user-row">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-t1">{u.name}</div>
                                        <div className="text-xs text-t4">{u.email}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Select value={u.tier} onValueChange={(v) => setPlan(u.id, v)}>
                                            <SelectTrigger data-testid={`admin-user-plan-${u.id}`} className="h-8 w-[120px] bg-surface-2"><SelectValue /></SelectTrigger>
                                            <SelectContent className="bg-surface-1">
                                                <SelectItem value="free">Free</SelectItem>
                                                <SelectItem value="pro">Pro</SelectItem>
                                                <SelectItem value="premium">Premium</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-4 py-3">{u.is_paying ? <Pill tone="success">{t("admin.users.yes")}</Pill> : <Pill>{t("admin.users.no")}</Pill>}</td>
                                    <td className="px-4 py-3 text-t3">{(u.created_at || "").slice(0, 10)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-2">
                                            <Button data-testid={`admin-user-paying-${u.id}`} size="sm" variant={u.is_paying ? "secondary" : "default"} className={cn("h-8 gap-1.5", u.is_paying && "bg-surface-3/70")} onClick={() => togglePaying(u)}>
                                                <BadgeCheck className="h-3.5 w-3.5" /> {u.is_paying ? t("admin.users.unmark") : t("admin.users.markPaying")}
                                            </Button>
                                            <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-t3" onClick={() => createPayout(u)}>
                                                <Wallet className="h-3.5 w-3.5" /> {t("admin.users.createPayout")}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ----- Referrals tab -----
const ReferralsTab = () => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (status = "") => {
        setLoading(true);
        try { const d = await adminApi.referrals(status); setRows(d.referrals || []); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(filter); }, [load, filter]);

    const decide = async (id, commission_status) => {
        try { const ref = await adminApi.updateReferral(id, { commission_status }); setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...ref } : r)); toast.success(t("admin.referrals.updated")); }
        catch (e) { toast.error("Error"); }
    };

    const commissionTone = { approved: "success", eligible: "warning", rejected: "muted", none: "muted" };

    return (
        <div data-testid="admin-referrals">
            <div className="mb-4 flex gap-2">
                {[["", "filterAll"], ["active", "filterActive"], ["pending", "filterPending"]].map(([val, key]) => (
                    <Button key={key} size="sm" variant={filter === val ? "default" : "secondary"} className={cn("h-8", filter !== val && "bg-surface-2")} onClick={() => setFilter(val)}>
                        {t(`admin.referrals.${key}`)}
                    </Button>
                ))}
            </div>
            {loading ? (
                <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
            ) : rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-10 text-center text-sm text-t3">{t("admin.referrals.empty")}</div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-surface-1">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead>
                            <tr className="border-b border-border text-left">
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.referrals.ambassador")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.referrals.member")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.referrals.status")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.referrals.commission")}</th>
                                <th className="px-4 py-3 mono-label text-t4 text-right">{t("admin.referrals.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid="admin-referral-row">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-t1">{r.ambassador_name || "—"}</div>
                                        <div className="text-xs text-t4">{r.ambassador_code}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-t1">{r.referred_name || "—"}</div>
                                        <div className="text-xs text-t4">{r.referred_email}</div>
                                    </td>
                                    <td className="px-4 py-3"><Pill tone={r.status === "active" ? "success" : r.status === "rejected" ? "muted" : "warning"}>{t(`ambassador.statuses.${r.status}`)}</Pill></td>
                                    <td className="px-4 py-3"><Pill tone={commissionTone[r.commission_status] || "muted"}>{t(`admin.commissionStatus.${r.commission_status || "none"}`)}</Pill></td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-2">
                                            <Button data-testid={`admin-referral-approve-${r.id}`} size="sm" className="h-8 gap-1.5" disabled={r.commission_status === "approved" || r.status !== "active"} onClick={() => decide(r.id, "approved")}>
                                                <Check className="h-3.5 w-3.5" /> {t("admin.referrals.approve")}
                                            </Button>
                                            <Button data-testid={`admin-referral-reject-${r.id}`} size="sm" variant="secondary" className="h-8 gap-1.5 bg-surface-3/70" disabled={r.commission_status === "rejected"} onClick={() => decide(r.id, "rejected")}>
                                                <X className="h-3.5 w-3.5" /> {t("admin.referrals.reject")}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ----- Payouts tab -----
const PayoutsTab = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const d = await adminApi.payouts(); setRows(d.payouts || []); } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setStatus = async (id, status) => {
        try { const p = await adminApi.updatePayout(id, { status }); setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...p } : r)); toast.success(t("admin.payouts.updated")); }
        catch (e) { toast.error("Error"); }
    };
    const tone = { paid: "success", approved: "info", pending: "warning", rejected: "muted" };

    return (
        <div data-testid="admin-payouts">
            {loading ? (
                <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
            ) : rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-10 text-center text-sm text-t3">{t("admin.payouts.empty")}</div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-border bg-surface-1">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead>
                            <tr className="border-b border-border text-left">
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.payouts.ambassador")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.payouts.amount")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.payouts.period")}</th>
                                <th className="px-4 py-3 mono-label text-t4">{t("admin.payouts.status")}</th>
                                <th className="px-4 py-3 mono-label text-t4 text-right">{t("admin.payouts.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => (
                                <tr key={p.id} className="border-b border-border/60 last:border-0" data-testid="admin-payout-row">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-t1">{p.ambassador_name || "—"}</div>
                                        <div className="text-xs text-t4">{p.ambassador_email}</div>
                                    </td>
                                    <td className="px-4 py-3 font-medium text-t1">{p.amount_eur} €</td>
                                    <td className="px-4 py-3 text-t3">{p.period || "—"}</td>
                                    <td className="px-4 py-3"><Pill tone={tone[p.status] || "muted"}>{t(`admin.payoutStatus.${p.status}`)}</Pill></td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-2">
                                            <Button size="sm" variant="secondary" className="h-8 bg-surface-3/70" disabled={p.status === "approved" || p.status === "paid"} onClick={() => setStatus(p.id, "approved")}>{t("admin.payouts.approve")}</Button>
                                            <Button data-testid={`admin-payout-paid-${p.id}`} size="sm" className="h-8" disabled={p.status === "paid"} onClick={() => setStatus(p.id, "paid")}>{t("admin.payouts.markPaid")}</Button>
                                            <Button size="sm" variant="ghost" className="h-8 text-t3" disabled={p.status === "rejected" || p.status === "paid"} onClick={() => setStatus(p.id, "rejected")}>{t("admin.payouts.reject")}</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default function Admin() {
    const { user } = useAuth();
    const { t } = useTranslation();

    if (!user?.is_admin) {
        return (
            <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="admin-not-authorized">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-t3"><ShieldCheck className="h-7 w-7" /></div>
                <h1 className="mt-4 font-sans-display text-xl font-semibold text-t1">{t("admin.notAuthorizedTitle")}</h1>
                <p className="mt-1.5 text-sm text-t3">{t("admin.notAuthorizedBody")}</p>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10" data-testid="admin-page">
            <div className="flex items-center gap-2.5">
                <ShieldCheck className="h-6 w-6 text-[hsl(var(--primary))]" />
                <div>
                    <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("admin.title")}</h1>
                    <p className="text-sm text-t3">{t("admin.subtitle")}</p>
                </div>
            </div>

            <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="bg-surface-2">
                    <TabsTrigger data-testid="admin-tab-overview" value="overview" className="gap-1.5"><TrendingUp className="h-4 w-4" /> {t("admin.tabs.overview")}</TabsTrigger>
                    <TabsTrigger data-testid="admin-tab-users" value="users" className="gap-1.5"><Users className="h-4 w-4" /> {t("admin.tabs.users")}</TabsTrigger>
                    <TabsTrigger data-testid="admin-tab-referrals" value="referrals" className="gap-1.5"><Gift className="h-4 w-4" /> {t("admin.tabs.referrals")}</TabsTrigger>
                    <TabsTrigger data-testid="admin-tab-payouts" value="payouts" className="gap-1.5"><Wallet className="h-4 w-4" /> {t("admin.tabs.payouts")}</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="mt-5"><OverviewTab /></TabsContent>
                <TabsContent value="users" className="mt-5"><UsersTab /></TabsContent>
                <TabsContent value="referrals" className="mt-5"><ReferralsTab /></TabsContent>
                <TabsContent value="payouts" className="mt-5"><PayoutsTab /></TabsContent>
            </Tabs>
        </div>
    );
}
